'use client';

import { ChevronDown } from 'lucide-react';
import posthog from 'posthog-js';

interface BuildingFAQItem {
  question: string;
  answer: string;
}

interface BuildingFAQProps {
  faqs: BuildingFAQItem[];
  /** Título del bloque (default: "Preguntas frecuentes del Edificio Nuevo Conquistador") */
  title?: string;
}

/**
 * FAQ accordion con tracking PostHog (`faq_opened`).
 *
 * NOTA: el JSON-LD FAQPage se inyecta desde el page.tsx (incluido en el `@graph`
 * principal). NO duplicar acá — Google ignora schemas duplicados y puede generar
 * warnings en Search Console.
 */
export default function BuildingFAQ({
  faqs,
  title = 'Preguntas frecuentes del Edificio Nuevo Conquistador',
}: BuildingFAQProps) {
  if (!faqs || faqs.length === 0) return null;

  const handleToggle = (question: string) => (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (!e.currentTarget.open) return;
    try {
      posthog.capture('faq_opened', {
        question,
        page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
    } catch {
      /* noop */
    }
  };

  return (
    <section
      aria-labelledby="building-faq-heading"
      className="scroll-reveal rounded-2xl card-elevated p-5 md:p-10"
    >
      <h2
        id="building-faq-heading"
        className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight mb-2"
      >
        {title}
      </h2>
      <p className="text-gray-600 text-base md:text-lg max-w-2xl mb-5 md:mb-7">
        Respuestas honestas a las dudas que recibimos por WhatsApp todos los días.
      </p>
      <div className="space-y-2.5">
        {faqs.map((faq, idx) => (
          <details
            key={idx}
            onToggle={handleToggle(faq.question)}
            className="group overflow-hidden border border-slate-200/70 bg-white/70 rounded-xl transition-all duration-200 hover:bg-stone-50/70 hover:border-slate-300/80 open:bg-stone-50/80 open:border-emerald-200/80"
          >
            <summary className="cursor-pointer flex min-h-[48px] w-full items-center justify-between gap-3 px-4 md:px-5 py-3.5 text-base font-semibold text-gray-900 list-none select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 rounded-xl">
              <span className="flex-1">{faq.question}</span>
              <span
                aria-hidden="true"
                className="flex-shrink-0 text-gray-500 transition-transform group-open:rotate-180"
              >
                <ChevronDown className="h-5 w-5" />
              </span>
            </summary>
            <p className="px-4 md:px-5 pb-4 pt-1 text-gray-700 leading-relaxed text-sm md:text-base">
              {faq.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}
